import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown, FileUp, Loader2, Trash2 } from "lucide-react";

const KIND_META = {
  task: { color: "#2563EB", label: "Task due" },
  milestone: { color: "#7C3AED", label: "Milestone" },
  meeting: { color: "#059669", label: "Meeting" },
  outlook: { color: "#EA580C", label: "Outlook" },
};
const pad = (n) => String(n).padStart(2, "0");

export default function DashboardCalendar() {
  const nav = useNavigate();
  const today = new Date();
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [open, setOpen] = useState(() => localStorage.getItem("dash_cal_open") !== "0");
  const fileRef = useRef(null);
  const toggle = () => setOpen((o) => { localStorage.setItem("dash_cal_open", o ? "0" : "1"); return !o; });

  const y = month.getFullYear(), m = month.getMonth();
  const start = `${y}-${pad(m + 1)}-01`;
  const end = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;

  const load = () => {
    setLoading(true);
    api.get(`/calendar/events?start=${start}&end=${end}`)
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [start, end]); // eslint-disable-line

  const importIcs = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/calendar/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} Outlook event${data.imported === 1 ? "" : "s"} imported to your calendar`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not import this file"); }
    finally { setImporting(false); }
  };

  const clearImported = async () => {
    if (!window.confirm("Remove all imported Outlook events from your calendar?")) return;
    try {
      const { data } = await api.delete("/calendar/imported");
      toast.success(`${data.deleted} imported event${data.deleted === 1 ? "" : "s"} removed`);
      load();
    } catch { toast.error("Could not clear imported events"); }
  };

  const byDate = {};
  events.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayIso = today.toISOString().slice(0, 10);
  const hasOutlook = events.some((e) => e.kind === "outlook");

  return (
    <div className="mt-8 rounded-2xl bg-white border border-border p-4 sm:p-5" data-testid="dashboard-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3">
          <button onClick={toggle} className="flex items-center gap-2 group" data-testid="cal-collapse-toggle" title={open ? "Collapse calendar" : "Expand calendar"}>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
            <h2 className="font-display text-lg font-bold flex items-center gap-2 group-hover:text-primary transition-colors"><CalendarDays className="h-5 w-5 text-primary" /> Calendar</h2>
          </button>
          {!open && <span className="text-xs text-muted-foreground" data-testid="cal-collapsed-summary">{events.length} event{events.length === 1 ? "" : "s"} in {month.toLocaleString("en-US", { month: "long" })}</span>}
          {open && (
          <div className="flex items-center gap-1">
            <button onClick={() => setMonth(new Date(y, m - 1, 1))} className="p-1 rounded hover:bg-secondary" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-semibold w-36 text-center" data-testid="cal-month-label">{month.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
            <button onClick={() => setMonth(new Date(y, m + 1, 1))} className="p-1 rounded hover:bg-secondary" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          )}
          {loading && open && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {open && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground">
            {Object.entries(KIND_META).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: v.color }} /> {v.label}</span>
            ))}
          </div>
          <input ref={fileRef} type="file" accept=".ics" className="hidden" onChange={(e) => { importIcs(e.target.files?.[0]); e.target.value = ""; }} data-testid="cal-ics-input" />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="cal-import-button">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Import Outlook (.ics)
          </Button>
          {hasOutlook && (
            <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={clearImported} data-testid="cal-clear-imported">
              <Trash2 className="h-3.5 w-3.5" /> Clear imported
            </Button>
          )}
        </div>
        )}
      </div>

      {open && (<>
      <div className="grid grid-cols-7 text-[10px] font-bold uppercase tracking-wide text-muted-foreground border-b border-border pb-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <span key={d} className="px-1.5">{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border border-border">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} className="bg-secondary/30 min-h-[86px]" />;
          const iso = `${y}-${pad(m + 1)}-${pad(d)}`;
          const dayEvents = byDate[iso] || [];
          const isToday = iso === todayIso;
          return (
            <div key={iso} className={`bg-white min-h-[86px] p-1 ${isToday ? "ring-2 ring-primary ring-inset" : ""}`} data-testid={`cal-day-${iso}`}>
              <span className={`text-[11px] font-bold px-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d}</span>
              <div className="space-y-0.5 mt-0.5">
                {dayEvents.slice(0, 3).map((e, j) => (
                  <button key={`${e.kind}-${e.id}-${j}`}
                    onClick={() => { if (e.project_id) nav(`/projects/${e.project_id}?tab=${e.kind === "meeting" ? "meeting" : "plan"}${e.kind !== "meeting" ? `&task=${e.id}` : ""}`); }}
                    title={`${KIND_META[e.kind]?.label}: ${e.title}${e.time ? ` · ${e.time}` : ""}${e.project ? ` · ${e.project}` : ""}${e.location ? ` · ${e.location}` : ""}`}
                    className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate block text-white font-medium ${e.project_id ? "cursor-pointer hover:opacity-85" : "cursor-default"}`}
                    style={{ background: KIND_META[e.kind]?.color || "#64748b" }}
                    data-testid={`cal-event-${e.id}`}>
                    {e.kind === "milestone" ? "◆ " : ""}{e.time ? `${e.time} ` : ""}{e.title}
                  </button>
                ))}
                {dayEvents.length > 3 && <span className="text-[9px] text-muted-foreground px-1 block">+{dayEvents.length - 3} more</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Shows open task due dates, milestones and meetings across your projects, plus anything you import from Outlook. In Outlook: File → Save Calendar → .ics, then import here.</p>
      </>)}
    </div>
  );
}
